import { useEffect, useState } from "react";
import {
  Users, GitBranch, Timer, Ban, Euro, ShieldCheck, Plus, Trash2, ArrowUp, ArrowDown, Check, Mail,
} from "lucide-react";
import api, { formatApiError, freshnessLabel } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const TABS = [
  { key: "users", label: "Utenti", icon: Users },
  { key: "pipeline", label: "Pipeline & Motivi", icon: GitBranch },
  { key: "cooldown", label: "Cooldown & Soglie", icon: Timer },
  { key: "exclusions", label: "Esclusioni", icon: Ban },
  { key: "costs", label: "Costi", icon: Euro },
  { key: "email", label: "Invio email", icon: Mail },
  { key: "gdpr", label: "GDPR", icon: ShieldCheck },
];

export default function Admin() {
  const [tab, setTab] = useState("users");
  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">Amministrazione</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-line rounded-[8px] p-1 flex-wrap h-auto">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`} className="data-[state=active]:bg-brand-soft data-[state=active]:text-brand-dark rounded-[6px] text-sm gap-1.5">
              <t.icon className="w-4 h-4" strokeWidth={1.5} /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
        <TabsContent value="pipeline" className="mt-6"><PipelineTab /></TabsContent>
        <TabsContent value="cooldown" className="mt-6"><CooldownTab /></TabsContent>
        <TabsContent value="exclusions" className="mt-6"><ExclusionsTab /></TabsContent>
        <TabsContent value="costs" className="mt-6"><CostsTab /></TabsContent>
        <TabsContent value="email" className="mt-6"><EmailTab /></TabsContent>
        <TabsContent value="gdpr" className="mt-6"><GdprTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function Card({ children, title, action }) {
  return (
    <div className="bg-white border border-line rounded-[8px] p-5">
      {(title || action) && (
        <div className="flex items-center justify-between mb-4">
          {title && <h2 className="text-sm font-semibold text-ink">{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

const btnPrimary = "flex items-center gap-1.5 bg-brand text-ink font-medium rounded-[6px] px-3 py-2 text-sm hover:bg-brand-hover transition-colors";
const inputCls = "bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-colors";

/* ---------------- Users ---------------- */
function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "sales" });
  const [toDelete, setToDelete] = useState(null);
  const [pwReset, setPwReset] = useState({ open: false, user: null, value: "", saving: false });
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const g = p.get("gmail");
    if (g) {
      if (g === "connected") toast.success("Account Google collegato all'utente");
      else if (g === "error") toast.error("Collegamento Google non riuscito");
      p.delete("gmail");
      window.history.replaceState({}, "", window.location.pathname + (p.toString() ? `?${p}` : ""));
      load();
    }
  }, []);
  const connectGoogle = async (u) => {
    try {
      const { data } = await api.get("/oauth/gmail/login", { params: { origin: window.location.origin, target_user_id: u.id } });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const create = async () => {
    try {
      await api.post("/admin/users", f);
      toast.success("Utente creato");
      setF({ name: "", email: "", password: "", role: "sales" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const update = async (id, patch) => { await api.put(`/admin/users/${id}`, patch); load(); };
  const doPwReset = async () => {
    if (!pwReset.value || pwReset.value.length < 6) return toast.error("Password troppo corta (min 6 caratteri)");
    setPwReset((m) => ({ ...m, saving: true }));
    try {
      await api.put(`/admin/users/${pwReset.user.id}`, { password: pwReset.value });
      toast.success(`Password reimpostata per ${pwReset.user.name}`);
      setPwReset({ open: false, user: null, value: "", saving: false });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPwReset((m) => ({ ...m, saving: false })); }
  };
  const remove = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/admin/users/${toDelete.id}`);
      toast.success("Utente eliminato");
      setToDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setToDelete(null); }
  };

  return (
    <div className="flex flex-col gap-6">
      <Card title="Nuovo utente">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <input data-testid="user-name" placeholder="Nome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} />
          <input data-testid="user-email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} />
          <input data-testid="user-password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} />
          <select data-testid="user-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={inputCls}>
            <option value="sales">Sales</option>
            <option value="admin">Admin</option>
          </select>
          <button data-testid="create-user-button" onClick={create} className={btnPrimary}><Plus className="w-4 h-4" strokeWidth={1.5} /> Crea</button>
        </div>
      </Card>
      <Card title={`Utenti (${users.length})`}>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-subtle border-b border-line"><th className="py-2 font-semibold">Nome</th><th className="py-2 font-semibold">Email</th><th className="py-2 font-semibold">Ruolo</th><th className="py-2 font-semibold">Account Google</th><th className="py-2 font-semibold">Stato</th><th className="py-2 font-semibold text-right">Azioni</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-line last:border-0">
                <td className="py-2.5 font-medium">{u.name}</td>
                <td className="py-2.5 text-subtle">{u.email}</td>
                <td className="py-2.5">
                  <select value={u.role} onChange={(e) => update(u.id, { role: e.target.value })} className="text-xs bg-white border border-line rounded-[6px] px-2 py-1">
                    <option value="sales">Sales</option><option value="admin">Admin</option>
                  </select>
                </td>
                <td className="py-2.5">
                  {u.gmail_accounts?.length ? (
                    <div className="flex flex-col gap-0.5" data-testid={`gmail-accounts-${u.id}`}>
                      {u.gmail_accounts.map((e, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 text-[11px] text-ink">
                          <span className="w-1.5 h-1.5 rounded-full bg-brand-dark shrink-0" />{e}{i === 0 && u.gmail_accounts.length > 1 ? " · principale" : ""}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span data-testid={`gmail-none-${u.id}`} className="text-[11px] text-faint">Non collegato</span>
                  )}
                </td>
                <td className="py-2.5"><span className={`text-[11px] rounded-full px-2 py-0.5 ${u.active ? "bg-brand-soft text-brand-dark" : "bg-[#FFE5E5] text-destructive"}`}>{u.active ? "Attivo" : "Disattivo"}</span></td>
                <td className="py-2.5 text-right">
                  <div className="inline-flex items-center gap-2">
                    <button data-testid={`toggle-user-${u.id}`} onClick={() => update(u.id, { active: !u.active })} className="text-xs bg-white border border-line rounded-[6px] px-2 py-1 hover:bg-background transition-colors">{u.active ? "Disattiva" : "Attiva"}</button>
                    <button data-testid={`connect-google-${u.id}`} onClick={() => connectGoogle(u)} className="text-xs bg-white border border-line rounded-[6px] px-2 py-1 hover:bg-background transition-colors">Collega Google</button>
                    <button data-testid={`reset-pw-${u.id}`} onClick={() => setPwReset({ open: true, user: u, value: "", saving: false })} className="text-xs bg-white border border-line rounded-[6px] px-2 py-1 hover:bg-background transition-colors">Reimposta password</button>
                    {u.id !== me?.id && (
                      <button data-testid={`delete-user-${u.id}`} onClick={() => setToDelete(u)} title="Elimina definitivamente" className="text-subtle hover:text-destructive transition-colors p-1"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'utente?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare definitivamente <strong>{toDelete?.name}</strong> ({toDelete?.email}).
              Le opportunità assegnate verranno liberate (non assegnate), ma contatti, aziende e segnali resteranno intatti. Questa azione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-user">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user" onClick={remove} className="bg-destructive text-white hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {pwReset.open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !pwReset.saving && setPwReset({ open: false, user: null, value: "", saving: false })}>
          <div data-testid="reset-pw-modal" className="bg-white rounded-[10px] w-full max-w-sm p-5 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="text-lg font-semibold text-ink">Reimposta password</h3>
              <p className="text-xs text-subtle mt-1">Nuova password per <strong>{pwReset.user?.name}</strong> ({pwReset.user?.email}).</p>
            </div>
            <input
              data-testid="reset-pw-input"
              type="text"
              autoFocus
              value={pwReset.value}
              onChange={(e) => setPwReset((m) => ({ ...m, value: e.target.value }))}
              placeholder="Nuova password (min 6)"
              className="bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
            <div className="flex justify-end gap-2">
              <button data-testid="reset-pw-cancel" onClick={() => setPwReset({ open: false, user: null, value: "", saving: false })} disabled={pwReset.saving} className="text-sm text-subtle hover:text-ink px-3 py-2">Annulla</button>
              <button data-testid="reset-pw-confirm" onClick={doPwReset} disabled={pwReset.saving || pwReset.value.length < 6} className="bg-brand text-ink font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-brand-hover disabled:opacity-50 transition-colors">
                {pwReset.saving ? "Salvo…" : "Reimposta"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Pipeline ---------------- */
function PipelineTab() {
  const [stages, setStages] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [newStage, setNewStage] = useState("");
  const [newReason, setNewReason] = useState("");

  const load = () => {
    api.get("/pipeline/stages").then((r) => setStages(r.data));
    api.get("/pipeline/loss-reasons").then((r) => setReasons(r.data));
  };
  useEffect(() => { load(); }, []);

  const addStage = async () => { if (!newStage.trim()) return; await api.post("/admin/stages", { name: newStage, marks_won: false }); setNewStage(""); load(); };
  const delStage = async (id) => { try { await api.delete(`/admin/stages/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggleWon = async (s) => { await api.put(`/admin/stages/${s.id}`, { name: s.name, marks_won: !s.marks_won }); load(); };
  const move = async (idx, dir) => {
    const arr = [...stages];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    await api.put("/admin/stages-reorder", { ordered_ids: arr.map((s) => s.id) });
    load();
  };
  const addReason = async () => { if (!newReason.trim()) return; await api.post("/admin/loss-reasons", { label: newReason }); setNewReason(""); load(); };
  const delReason = async (id) => { await api.delete(`/admin/loss-reasons/${id}`); load(); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card title="Fasi pipeline">
        <div className="flex flex-col gap-2 mb-4">
          {stages.map((s, i) => (
            <div key={s.id} data-testid={`stage-item-${s.key}`} className="flex items-center gap-2 border border-line rounded-[6px] p-2.5">
              <div className="flex flex-col">
                <button onClick={() => move(i, -1)} className="text-subtle hover:text-ink transition-colors"><ArrowUp className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                <button onClick={() => move(i, 1)} className="text-subtle hover:text-ink transition-colors"><ArrowDown className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
              </div>
              <span className="text-sm font-medium flex-1">{s.name}</span>
              <button onClick={() => toggleWon(s)} className={`text-[11px] rounded-full px-2 py-0.5 border transition-colors ${s.marks_won ? "bg-brand-soft text-brand-dark border-transparent" : "border-line text-subtle hover:bg-background"}`}>
                {s.marks_won ? "→ segna Vinto" : "no esito"}
              </button>
              <button data-testid={`del-stage-${s.key}`} onClick={() => delStage(s.id)} className="text-subtle hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input data-testid="new-stage-input" value={newStage} onChange={(e) => setNewStage(e.target.value)} placeholder="Nuova fase" className={`${inputCls} flex-1`} />
          <button data-testid="add-stage-button" onClick={addStage} className={btnPrimary}><Plus className="w-4 h-4" strokeWidth={1.5} /></button>
        </div>
      </Card>

      <Card title="Motivi di perdita">
        <div className="flex flex-wrap gap-2 mb-4">
          {reasons.map((r) => (
            <span key={r.id} data-testid={`reason-${r.id}`} className="inline-flex items-center gap-1.5 bg-background border border-line rounded-full px-3 py-1 text-xs">
              {r.label}
              <button onClick={() => delReason(r.id)} className="text-faint hover:text-destructive transition-colors"><Trash2 className="w-3 h-3" strokeWidth={1.5} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input data-testid="new-reason-input" value={newReason} onChange={(e) => setNewReason(e.target.value)} placeholder="Nuovo motivo" className={`${inputCls} flex-1`} />
          <button data-testid="add-reason-button" onClick={addReason} className={btnPrimary}><Plus className="w-4 h-4" strokeWidth={1.5} /></button>
        </div>
      </Card>
    </div>
  );
}

/* ---------------- Cooldown ---------------- */
function CooldownTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => { await api.put("/admin/settings", s); toast.success("Impostazioni salvate"); };
  const field = (k, label, sub) => (
    <div>
      <label className="text-sm font-semibold text-ink block mb-1.5">{label}</label>
      <input data-testid={`setting-${k}`} type="number" value={s[k]} onChange={(e) => setS({ ...s, [k]: parseFloat(e.target.value) })} className={`${inputCls} w-full`} />
      {sub && <p className="text-[11px] text-faint mt-1">{sub}</p>}
    </div>
  );
  return (
    <Card title="Cooldown, soglie e retention">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl">
        {field("cooldown_won_days", "Cooldown Vinto (giorni)", "Giorni prima di riattivare un'azienda vinta")}
        {field("cooldown_lost_days", "Cooldown Perso (giorni)", "Giorni prima di riattivare un'azienda persa")}
        {field("cost_threshold_eur", "Soglia costo enrichment (€)", "Alert quando la spesa la supera")}
        {field("retention_months", "Retention record (mesi)", "Alert revisione dopo N mesi senza attività")}
        {field("import_limit", "Limite importazione annunci", "Max annunci importati per attivazione posizione (controllo costi TheirStack)")}
      </div>
      <button data-testid="save-settings-button" onClick={save} className={`${btnPrimary} mt-5`}><Check className="w-4 h-4" strokeWidth={1.5} /> Salva</button>
    </Card>
  );
}

/* ---------------- Email sending ---------------- */
const WEEKDAYS = [["Lun", 0], ["Mar", 1], ["Mer", 2], ["Gio", 3], ["Ven", 4], ["Sab", 5], ["Dom", 6]];

function EmailTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/email-settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => {
    try { await api.put("/admin/email-settings", s); toast.success("Impostazioni email salvate"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleDay = (d) => {
    const days = s.email_send_days.includes(d) ? s.email_send_days.filter((x) => x !== d) : [...s.email_send_days, d];
    setS({ ...s, email_send_days: days.sort() });
  };
  const num = (k, label, sub) => (
    <div>
      <label className="text-sm font-semibold text-ink block mb-1.5">{label}</label>
      <input data-testid={`email-setting-${k}`} type="number" value={s[k]} onChange={(e) => setS({ ...s, [k]: parseInt(e.target.value || "0", 10) })} className={`${inputCls} w-full`} />
      {sub && <p className="text-[11px] text-faint mt-1">{sub}</p>}
    </div>
  );
  return (
    <Card title="Limiti invio email (per casella Google)">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl">
        {num("email_daily_cap", "Limite giornaliero per casella", "Numero massimo di email inviate al giorno da ogni casella")}
        <div>
          <label className="text-sm font-semibold text-ink block mb-1.5">Giorni di invio</label>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAYS.map(([lbl, d]) => (
              <button key={d} data-testid={`email-day-${d}`} onClick={() => toggleDay(d)} className={`text-xs rounded-[6px] px-2.5 py-1.5 border transition-colors ${s.email_send_days.includes(d) ? "bg-brand-soft border-brand-soft text-brand-dark font-medium" : "bg-white border-line text-subtle"}`}>{lbl}</button>
            ))}
          </div>
        </div>
        {num("email_hour_start", "Ora inizio invii", "Formato 24h (es. 9)")}
        {num("email_hour_end", "Ora fine invii", "Formato 24h (es. 18)")}
        {num("email_delay_min", "Delay minimo tra invii (sec)", "Attesa minima tra un'email e l'altra")}
        {num("email_delay_max", "Delay massimo tra invii (sec)", "Attesa massima (viene scelto un valore casuale nell'intervallo)")}
      </div>
      <p className="text-[11px] text-faint mt-3">Fuso orario di riferimento: Europe/Rome. Il delay casuale evita pattern robotici riconoscibili.</p>
      <button data-testid="save-email-settings-button" onClick={save} className={`${btnPrimary} mt-5`}><Check className="w-4 h-4" strokeWidth={1.5} /> Salva</button>
    </Card>
  );
}

/* ---------------- Exclusions ---------------- */
function ExclusionsTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/exclusions").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const include = async (id) => { await api.post(`/admin/companies/${id}/include`); toast.success("Rimossa dalle esclusioni"); load(); };
  return (
    <Card title={`Lista Esclusioni (${items.length})`}>
      {items.length === 0 ? <p className="text-xs text-faint">Nessuna azienda esclusa. Puoi escludere un'azienda dal dettaglio opportunità.</p> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-subtle border-b border-line"><th className="py-2 font-semibold">Azienda</th><th className="py-2 font-semibold">Motivo</th><th className="py-2 font-semibold text-right"></th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} data-testid={`exclusion-${c.id}`} className="border-b border-line last:border-0">
                <td className="py-2.5 font-medium">{c.name}</td>
                <td className="py-2.5 text-subtle text-xs">{c.exclusion_reason || "—"}</td>
                <td className="py-2.5 text-right"><button onClick={() => include(c.id)} className="text-xs bg-white border border-line rounded-[6px] px-2 py-1 hover:bg-background transition-colors">Riabilita</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/* ---------------- Costs ---------------- */
function CostsTab() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(30);
  useEffect(() => { api.get(`/admin/costs?period_days=${period}`).then((r) => setData(r.data)); }, [period]);
  if (!data) return null;
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <select data-testid="cost-period" value={period} onChange={(e) => setPeriod(parseInt(e.target.value))} className={inputCls}>
          <option value={7}>Ultimi 7 giorni</option>
          <option value={30}>Ultimi 30 giorni</option>
          <option value={90}>Ultimi 90 giorni</option>
        </select>
        {data.over_threshold && (
          <span className="inline-flex items-center gap-1.5 bg-[#FFE5E5] text-destructive rounded-full px-3 py-1 text-xs font-medium">
            Soglia superata ({data.threshold_eur}€)
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><p className="text-xs text-subtle">Spesa totale</p><p data-testid="total-cost" className="text-2xl font-bold text-ink mt-1">{data.total_eur}€</p></Card>
        <Card><p className="text-xs text-subtle">Crediti consumati</p><p className="text-2xl font-bold text-ink mt-1">{data.total_credits}</p></Card>
        <Card><p className="text-xs text-subtle">Arricchimenti</p><p className="text-2xl font-bold text-ink mt-1">{data.count}</p></Card>
      </div>
      <Card title="Spesa giornaliera (€)">
        {data.daily.length === 0 ? <p className="text-xs text-faint">Nessun dato nel periodo.</p> : (
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E7E3" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6B6B6B" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#6B6B6B" }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E8E7E3", fontSize: 12 }} />
                <Bar dataKey="cost_eur" fill="#00E5A0" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Per utente">
          {data.by_user.map((u) => (
            <div key={u.name} className="flex justify-between text-sm py-1.5 border-b border-line last:border-0"><span>{u.name}</span><span className="font-medium">{u.cost_eur}€ · {u.count}</span></div>
          ))}
          {data.by_user.length === 0 && <p className="text-xs text-faint">—</p>}
        </Card>
        <Card title="Per posizione">
          {data.by_position.map((p) => (
            <div key={p.name} className="flex justify-between text-sm py-1.5 border-b border-line last:border-0"><span>{p.name}</span><span className="font-medium">{p.cost_eur}€ · {p.count}</span></div>
          ))}
          {data.by_position.length === 0 && <p className="text-xs text-faint">—</p>}
        </Card>
      </div>
    </div>
  );
}

/* ---------------- GDPR ---------------- */
function GdprTab() {
  const [logs, setLogs] = useState([]);
  const [retention, setRetention] = useState(null);
  const load = () => {
    api.get("/admin/gdpr/erasure-logs").then((r) => setLogs(r.data));
    api.get("/admin/gdpr/retention-review").then((r) => setRetention(r.data));
  };
  useEffect(() => { load(); }, []);
  const eraseCompany = async (id) => { if (!window.confirm("Cancellare definitivamente azienda e dati collegati?")) return; await api.post(`/admin/gdpr/erase-company/${id}`); toast.success("Azienda cancellata"); load(); };
  return (
    <div className="flex flex-col gap-6">
      <Card title="Diritto all'oblio — retention review">
        <p className="text-xs text-subtle mb-3">Aziende senza attività oltre la soglia di retention, senza opportunità aperte o vinte.</p>
        {retention?.companies?.length ? (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-subtle border-b border-line"><th className="py-2 font-semibold">Azienda</th><th className="py-2 font-semibold">Ultimo segnale</th><th className="py-2 font-semibold text-right"></th></tr></thead>
            <tbody>
              {retention.companies.map((c) => (
                <tr key={c.id} data-testid={`retention-${c.id}`} className="border-b border-line last:border-0">
                  <td className="py-2.5 font-medium">{c.name}</td>
                  <td className="py-2.5 text-xs text-subtle">{freshnessLabel(c.last_signal_at)}</td>
                  <td className="py-2.5 text-right"><button onClick={() => eraseCompany(c.id)} className="text-xs bg-destructive text-white rounded-[6px] px-2 py-1 hover:bg-[#E03B3B] transition-colors">Cancella</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="text-xs text-faint">Nessuna azienda da revisionare.</p>}
      </Card>
      <Card title={`Log cancellazioni (${logs.length})`}>
        {logs.length === 0 ? <p className="text-xs text-faint">Nessuna cancellazione registrata.</p> : logs.map((l) => (
          <div key={l.id} className="flex justify-between text-sm py-1.5 border-b border-line last:border-0">
            <span>{l.kind === "company" ? "Azienda" : "Contatto"}: <b>{l.target_label}</b></span>
            <span className="text-xs text-faint">{l.by} · {freshnessLabel(l.timestamp)}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}
